import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import Select from 'react-select';
import { 
    FaUser, 
    FaEnvelope, 
    FaLock, 
    FaPhone, 
    FaBriefcase, 
    FaCalendar, 
    FaMoneyBillWave,
    FaHome,
    FaGraduationCap,
    FaPlus,
    FaTrash,
    FaArrowLeft,
    FaArrowRight,
    FaInfoCircle,
    FaSpinner,
    FaSave,
    FaMapMarkerAlt
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './CreateHrManager.css';
import { AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogActions, Button, Modal, Box, DialogTitle } from '@mui/material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import UpdateHrManager from './UpdateHrManager';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';


const CreateEmployee = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '+263',
        position: '',
        department: '',
        contract_type: 'fixed_term',
        contract_start_date: '',
        contract_end_date: '',
        probation_period: '',
        probation_end_date: '',
        salary: '',
        working_days_per_month: '',
        date_of_birth: '',
        next_of_kin_name: '',
        next_of_kin_contact: '+263',
        next_of_kin_address: '',
        next_of_kin_relationship: '',
        photo: null,
        photo_preview: null,
        gender: '',
        home_address: '',
        marital_status: '',
        category: 'worker',
        allowances: [{ allowance_type: '', amount: '', title: '' }],
        qualifications: [{ title: '' }],
        documents: [{ document_type: '', document_file: null }],

    });
    const [errors, setErrors] = useState({});
    const [calculatedAge, setCalculatedAge] = useState(null);
    const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 1 });
    const [imageRef, setImageRef] = useState(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/departments/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const departmentOptions = response.data.map(dept => ({
                value: dept.id,
                label: dept.name
            }));
            
            setDepartments(departmentOptions);
        } catch (error) {
            console.error('Error fetching departments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch departments',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    // Helper functions for date formatting
    const formatDateForInput = (dateString) => {
        if (!dateString) return null;
        return dayjs(dateString);
    };

    const email = formData.first_name + formData.last_name + '@burrayhr.com';

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = dayjs(dateString);
        return date.format('DD/MM/YYYY');
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return null;
        
        const today = dayjs();
        const birth = dayjs(birthDate);
        
        // Get the birth date components
        const birthYear = birth.year();
        const birthMonth = birth.month();
        const birthDay = birth.date();
        
        // Get today's date components
        const currentYear = today.year();
        const currentMonth = today.month();
        const currentDay = today.date();
        
        // Calculate the age
        let age = currentYear - birthYear;
        
        // Adjust age if birthday hasn't occurred yet this year
        if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
            age--;
        }
        
        // Validate age based on contract type
        const minAge = formData.contract_type === 'student' ? 16 : 18;
        const maxAge = 100;
        
        if (age < minAge || age > maxAge) {
            return null;
        }
        
        return age;
    };

    const handleDateChange = (date, fieldName) => {
        if (date) {
            setFormData(prev => ({
                ...prev,
                [fieldName]: date.toISOString()
            }));
            
            // Recalculate age if the field is date_of_birth
            if (fieldName === 'date_of_birth') {
                const age = calculateAge(date);
                setCalculatedAge(age);
                
                // Validate age immediately
                if (age === null) {
                    const minAge = formData.contract_type === 'student' ? 16 : 18;
                    setErrors(prev => ({
                        ...prev,
                        date_of_birth: `Age must be at least ${minAge} years for ${formData.contract_type} contract type`
                    }));
                } else {
                    setErrors(prev => ({
                        ...prev,
                        date_of_birth: ''
                    }));
                }
            }
            
            // Recalculate probation end date if contract start date changes and is valid
            if (fieldName === 'contract_start_date' && formData.probation_period) {
                const today = dayjs();
                const startDate = dayjs(date);
                
                if (startDate.isAfter(today) || startDate.isSame(today, 'day')) {
                    const endDate = calculateProbationEndDate(date, formData.probation_period);
                    setFormData(prev => ({
                        ...prev,
                        probation_end_date: endDate ? endDate.toISOString() : ''
                    }));
                } else {
                    // If start date is in the past, clear probation end date and show error
                    setFormData(prev => ({
                        ...prev,
                        probation_end_date: ''
                    }));
                    setErrors(prev => ({
                        ...prev,
                        contract_start_date: 'Contract start date must be today or a future date to automatically set probation end date'
                    }));
                }
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: ''
            }));
            if (fieldName === 'date_of_birth') {
                setCalculatedAge(null);
                setErrors(prev => ({
                    ...prev,
                    date_of_birth: 'Date of birth is required'
                }));
            }
            if (fieldName === 'contract_start_date') {
                // Clear probation end date if contract start date is cleared
                setFormData(prev => ({
                    ...prev,
                    probation_end_date: ''
                }));
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'contract_type') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                // Reset probation fields when changing contract type
                probation_period: '',
                probation_end_date: ''
            }));
            
            // Recalculate age if we have a date of birth
            if (formData.date_of_birth) {
                const age = calculateAge(formData.date_of_birth);
                setCalculatedAge(age);
                
                // If age is invalid for new contract type, show error
                if (age === null) {
                    const minAge = value === 'student' ? 16 : 18;
                    setErrors(prev => ({
                        ...prev,
                        date_of_birth: `Age must be at least ${minAge} years for ${value} contract type`
                    }));
                } else {
                    // Clear date of birth error if age is valid
                    setErrors(prev => ({
                        ...prev,
                        date_of_birth: ''
                    }));
                }
            }
        } else if (name === 'probation_period') {
            const today = dayjs();
            const startDate = formData.contract_start_date ? dayjs(formData.contract_start_date) : null;
            
            // Only calculate probation end date if start date is today or in the future
            if (startDate && (startDate.isAfter(today) || startDate.isSame(today, 'day'))) {
                if (formData.contract_type === 'open' && value) {
                    // For open contracts, force 3 months if probation is selected
                    const calculatedEndDate = calculateProbationEndDate(formData.contract_start_date, '3');
                    
                    setFormData(prev => ({
                        ...prev,
                        probation_period: '3',
                        probation_end_date: calculatedEndDate ? calculatedEndDate.toISOString() : ''
                    }));
                } else if (formData.contract_type === 'fixed_term') {
                    // For fixed term contracts, allow selection of probation period
                    const calculatedEndDate = calculateProbationEndDate(formData.contract_start_date, value);
                    
                    setFormData(prev => ({
                        ...prev,
                        probation_period: value,
                        probation_end_date: calculatedEndDate ? calculatedEndDate.toISOString() : ''
                    }));
                }
            } else {
                // If start date is in the past or not set, clear probation end date and show error
                setFormData(prev => ({
                    ...prev,
                    probation_period: value,
                    probation_end_date: ''
                }));
                setErrors(prev => ({
                    ...prev,
                    contract_start_date: 'Contract start date must be today or a future date to set probation period'
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDepartmentChange = (selectedOption) => {
        setSelectedDepartment(selectedOption);
        setFormData(prev => ({
            ...prev,
            department: selectedOption?.value || ''
        }));
        
        if (errors.department) {
            setErrors(prev => ({
                ...prev,
                department: null
            }));
        }
    };


    const handleAddDocument = () => {
        setFormData(prev => ({
            ...prev,
            documents: [...prev.documents, { document_type: '', document_file: null }]
        }));
    };

    const handleRemoveDocument = (index) => {
        setFormData(prev => ({
            ...prev,
            documents: prev.documents.filter((_, i) => i !== index)
        }));
    };

    const handleDocumentChange = (index, field, value) => {
        const newDocuments = [...formData.documents];
        newDocuments[index][field] = value;
        setFormData(prev => ({
            ...prev,
            documents: newDocuments
        }));
    };

    const handleFileChange = (index, file) => {
        if (file) {
            console.log('File selected:', file);
            const newDocuments = [...formData.documents];
            newDocuments[index] = {
                ...newDocuments[index],
                document_file: file
            };
            setFormData(prev => ({
                ...prev,
                documents: newDocuments
            }));
        }
    };

    const handleAddQualification = () => {
        setFormData(prev => ({
            ...prev,
            qualifications: [...prev.qualifications, { title: '' }]
        }));
    };

    const handleRemoveQualification = (index) => {
        setFormData(prev => ({
            ...prev,
            qualifications: prev.qualifications.filter((_, i) => i !== index)
        }));
    };

    const handleQualificationChange = (index, field, value) => {
        const newQualifications = [...formData.qualifications];
        newQualifications[index][field] = value;
        setFormData(prev => ({
            ...prev,
            qualifications: newQualifications
        }));
    };

    const handleAllowanceChange = (index, field, value) => {
        const newAllowances = [...formData.allowances];
        newAllowances[index][field] = value;
        
        // Reset title when allowance type changes
        if (field === 'allowance_type' && value !== 'other') {
            newAllowances[index].title = '';
        }
        
        setFormData(prev => ({
            ...prev,
            allowances: newAllowances
        }));
    };

    const handleAddAllowance = () => {
        setFormData(prev => ({
            ...prev,
            allowances: [...prev.allowances, { allowance_type: '', amount: '', title: '' }]
        }));
    };

    const handleRemoveAllowance = (index) => {
        setFormData(prev => ({
            ...prev,
            allowances: prev.allowances.filter((_, i) => i !== index)
        }));
    };

    const validateCurrentSlide = () => {
        const newErrors = {};
        
        switch (currentSlide) {
            case 0: // Basic Information  
                if (!formData.first_name) newErrors.first_name = 'First name is required';
                if (!formData.last_name) newErrors.last_name = 'Last name is required';
                if (!formData.phone_number) newErrors.phone_number = 'Phone number is required';
                if (!formData.department) newErrors.department = 'Department is required';
                if (!formData.contract_type) newErrors.contract_type = 'Contract type is required';
                break;

            case 1: // Contract Information
                if (!formData.contract_start_date) {
                    newErrors.contract_start_date = 'Start date is required';
                }
                if (!formData.salary) {
                    newErrors.salary = 'Salary is required';
                }
                // Check end date for both fixed_term and student contracts
                if (formData.contract_type === 'fixed_term' || formData.contract_type === 'student') {
                    if (!formData.contract_end_date || formData.contract_end_date.trim() === '') {
                        newErrors.contract_end_date = `End date is required for ${formData.contract_type === 'student' ? 'student' : 'fixed-term'} contracts`;
                    } else {
                        const startDate = new Date(formData.contract_start_date);
                        const endDate = new Date(formData.contract_end_date);
                        if (endDate <= startDate) {
                            newErrors.contract_end_date = 'End date must be after start date';
                        }
                    }
                }
                break;

            case 2: // Personal Information
                if (!formData.date_of_birth) {
                    newErrors.date_of_birth = 'Date of birth is required';
                } else {
                    const age = calculateAge(formData.date_of_birth);
                    if (age === null) {
                        const minAge = formData.contract_type === 'student' ? 16 : 18;
                        newErrors.date_of_birth = `Age must be at least ${minAge} years for ${formData.contract_type} contract type`;
                    }
                }
                if (!formData.gender) newErrors.gender = 'Gender is required';
                if (!formData.marital_status) newErrors.marital_status = 'Marital status is required';
                
                break;

            case 3: // Children Information
            if (!formData.next_of_kin_name) {
                newErrors.next_of_kin_name = 'Next of kin name is required';
            }
            if (!formData.next_of_kin_contact) {
                newErrors.next_of_kin_contact = 'Next of kin contact is required';
            }
            if (!formData.next_of_kin_address) {
                newErrors.next_of_kin_address = 'Next of kin address is required';
            }
            if (!formData.next_of_kin_relationship) {
                newErrors.next_of_kin_relationship = 'Next of kin relationship is required';
            }
                
                    
                break;

            case 4: // Documents Information
                formData.documents.forEach((doc, index) => {
                    if (doc.document_type.trim() && !doc.document_file) {
                        newErrors[`document_${index}_file`] = 'Document file is required when document type is selected';
                    }
                });
                break;
        }
        
        setErrors(newErrors);
        
        // If there are errors, show a notification
        if (Object.keys(newErrors).length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields correctly before proceeding.',
                confirmButtonColor: '#1e3a8a'
            });
        }
        
        return Object.keys(newErrors).length === 0;
    };

    const handleSlideChange = (direction) => {
        if (direction === 'next') {
            if (validateCurrentSlide()) {
                // Add a small delay to allow the exit animation to complete
                setTimeout(() => {
                    setCurrentSlide(prev => prev + 1);
                }, 50);
            }
        } else {
            setTimeout(() => {
                setCurrentSlide(prev => prev - 1);
            }, 50);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateCurrentSlide()) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();


            // Add qualifications data
            const validQualifications = formData.qualifications.filter(qual => 
                qual.title && qual.title.trim() !== ''
            ).map(qual => ({
                title: qual.title.trim()
            }));

            // Add allowances data
            const validAllowances = formData.allowances.filter(allowance => 
                allowance.allowance_type && allowance.allowance_type.trim() !== '' && 
                allowance.amount && allowance.amount.toString().trim() !== '' &&
                (allowance.allowance_type !== 'other' || (allowance.title && allowance.title.trim() !== ''))
            ).map(allowance => ({
                allowance_type: allowance.allowance_type.trim(),
                amount: parseFloat(allowance.amount),
                title: allowance.allowance_type === 'other' ? allowance.title.trim() : ''
            }));
            
            // Create the request data object
            const requestData = {
                email: email,
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone_number: formData.phone_number,
                department: formData.department,
                position: formData.position,
                contract_type: formData.contract_type,
                contract_start_date: formData.contract_start_date ? dayjs(formData.contract_start_date).format('YYYY-MM-DD') : '',
                contract_end_date: formData.contract_end_date ? dayjs(formData.contract_end_date).format('YYYY-MM-DD') : '',
                probation_period: formData.probation_period,
                probation_end_date: formData.probation_end_date ? dayjs(formData.probation_end_date).format('YYYY-MM-DD') : '',
                salary: formData.salary,
                working_days_per_month: formData.working_days_per_month,
                date_of_birth: formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : '',
                gender: formData.gender,
                home_address: formData.home_address,
                qualifications: JSON.stringify(validQualifications),
                allowances: JSON.stringify(validAllowances),
                next_of_kin_name: formData.next_of_kin_name,
                next_of_kin_contact: formData.next_of_kin_contact,
                next_of_kin_address: formData.next_of_kin_address,
                next_of_kin_relationship: formData.next_of_kin_relationship,
                marital_status: formData.marital_status,
                category: 'worker'
            };

            // Append all text fields from requestData
            Object.keys(requestData).forEach(key => {
                formDataToSend.append(key, requestData[key] || '');
            });

            // Add documents in the required format
            const validDocuments = formData.documents.filter(doc => 
                doc.document_type && doc.document_type.trim() !== '' && 
                doc.document_file instanceof File
            );

            // Log document files for debugging
            
            // Add document types as an array
            validDocuments.forEach(doc => {
                formDataToSend.append('document_types', doc.document_type.trim());
            });
            
            // Add document files as an array
            validDocuments.forEach(doc => {
                if (doc.document_file instanceof File) {
                    formDataToSend.append('uploaded_documents', doc.document_file);
                } else {
                    console.warn('Document file is not a File object:', doc.document_file);
                }
            });
            
            // Add document descriptions if needed
            validDocuments.forEach(doc => {
                if (doc.description && doc.description.trim() !== '') {
                    formDataToSend.append('document_descriptions', doc.description.trim());
                    console.log('Adding document description:', doc.description.trim());
                }
            });

            // Add photo if it exists - directly append the File object
            if (formData.photo) {
                try {
                    // Check if photo is a File or Blob object
                    if (formData.photo instanceof File) {
                        formDataToSend.append('photo', formData.photo);
                        console.log('Adding photo as File:', formData.photo.name);
                    } else if (formData.photo instanceof Blob) {
                        // Convert Blob to File if needed
                        const photoFile = new File([formData.photo], 'profile_photo.jpg', { type: 'image/jpeg' });
                        formDataToSend.append('photo', photoFile);
                        console.log('Adding photo as converted File from Blob');
                    } else {
                        console.warn('Photo is neither a File nor a Blob object:', formData.photo);
                    }
                } catch (error) {
                    console.error('Error processing photo:', error);
                }
            } else if (croppedImageUrl) {
                // If we have a cropped image URL but no photo in formData, try to fetch it from the URL
                try {
                    const response = await fetch(croppedImageUrl);
                    const blob = await response.blob();
                    const photoFile = new File([blob], 'profile_photo.jpg', { type: 'image/jpeg' });
                    formDataToSend.append('photo', photoFile);
                    console.log('Adding photo from cropped image URL');
                } catch (error) {
                    console.error('Error fetching photo from cropped URL:', error);
                }
            }

            const response = await axios.post(
                `${baseUrl}/api/employees/`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Employee created successfully',
                confirmButtonColor: '#1e3a8a'
            });
            
            navigate('/admin-manager/employees');
            
        } catch (error) {
            let errorMessage = 'Unable to create employee. Please try again.';
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Check for duplicate email error
                if (errorData.error && typeof errorData.error === 'string') {
                    const error = errorData.error.toLowerCase();
                    if (error.includes('duplicate') && error.includes('email')) {
                        errorMessage = 'Email already taken. Please try another one.';
                    }
                }
            }

            // Show the error message
            Swal.fire({
                icon: 'error',
                title: 'Cannot Create Employee',
                text: errorMessage,
                confirmButtonColor: '#1e3a8a'
            });
            console.log('Error creating employee:', error);
        } finally {
            setLoading(false);
        }
    };

    const onImageLoad = (image) => {
        // Store the image element reference for cropping
        setImageRef(image.currentTarget);
    };

    const handleCropComplete = (crop) => {
        setCrop(crop);
    };

    const getCroppedImg = async (image, crop, fileName) => {
        if (!image || !crop.width || !crop.height) {
            console.error('Invalid image or crop dimensions');
            return null;
        }

        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error('Canvas is empty');
                    reject(new Error('Canvas is empty'));
                    return;
                }
                // Create a File object from the blob for direct upload
                const file = new File([blob], fileName, { type: 'image/jpeg' });
                const previewUrl = window.URL.createObjectURL(blob);
                resolve({ file, previewUrl });
            }, 'image/jpeg', 0.95); // Higher quality JPEG
        });
    };

    const handleCropSave = async () => {
        if (!imageRef) {
            console.error('No image reference available');
            return;
        }

        if (!crop.width || !crop.height) {
            console.error('Invalid crop dimensions', crop);
            return;
        }

        try {
            console.log('Starting crop with imageRef:', imageRef);
            const result = await getCroppedImg(
                imageRef,
                crop,
                'profile_photo.jpg'
            );
            
            if (!result) {
                console.error('Failed to get cropped image');
                return;
            }
            
            const { file, previewUrl } = result;
            
            console.log('Crop successful, updating form data with file:', file);
            setFormData(prev => ({
                ...prev,
                photo: file,
                photo_preview: previewUrl
            }));
            setCroppedImageUrl(previewUrl);
            setImageDialogOpen(false);
        } catch (e) {
            console.error('Error cropping image:', e);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to crop image. Please try again.',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    // Add function to calculate probation end date
    const calculateProbationEndDate = (startDate, period) => {
        if (!startDate || !period) return null;
        return dayjs(startDate).add(parseInt(period), 'month');
    };

  return (
    <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="create-hr-manager-container">
                    <div className="page-header">
                        <div className="header-content">
                            <h1>
                                <FaUser style={{color: 'navy'}} className="" />
                                Add Employee
                            </h1>
                            <p className="header-description">
                                Create a new Employee account
                            </p>
    </div>
                        <button 
                            className="back-button"
                            onClick={() => navigate('/admin-manager/employees')}
                        >
                            <FaArrowLeft /> Back to Employees
                        </button>
                    </div>
                    
                    <div className="form-container">
                        <div className="form-progress">
                            <div className="progress-steps">
                                {[
                                    { label: 'Basic Info', step: 0 },
                                    { label: 'Contract', step: 1 },
                                    { label: 'Personal', step: 2 },
                                    { label: 'Next of Kin Details', step: 3 },
                                    { label: 'Documents', step: 4 }
                                ].map(({ label, step }) => (
                                    <div key={step} className={`progress-step ${currentSlide === step ? 'active' : ''}`}>
                                        <div className="step-number">{step + 1}</div>
                                        <div className="step-label">{label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="form-content">
                            <div className="form-slides-container">
                                <AnimatePresence mode="wait">
                                    {currentSlide === 0 && (
                                        <motion.div
                                            key="slide-1"
                                            className="form-slide"
                                            initial={{ opacity: 0, x: '100%' }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: '-100%' }}
                                            transition={{ type: 'tween', duration: 0.3 }}
                                        >
                                            
                                            <div className="form-group">
                                                <label htmlFor="first_name">
                                                    <FaUser className="field-icon" />
                                                    First Name <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="first_name"
                                                    name="first_name"
                                                    value={formData.first_name}
                                                    onChange={handleChange}
                                                    placeholder="Enter first name"
                                                    className={errors.first_name ? 'error' : ''}
                                                />
                                                {errors.first_name && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.first_name}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="last_name">
                                                    <FaUser className="field-icon" />
                                                    Last Name <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    id="last_name"
                                                    name="last_name"
                                                    value={formData.last_name}
                                                    onChange={handleChange}
                                                    placeholder="Enter last name"
                                                    className={errors.last_name ? 'error' : ''}
                                                />
                                                {errors.last_name && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.last_name}
                                                    </div>
                                                )}
                                            </div>

                                            
                                            <div className="form-group">
                                                <label htmlFor="department">
                                                    <FaBriefcase className="field-icon" />
                                                    Department <span className="required">*</span>
                                                </label>
                                                <Select
                                                    id="department"
                                                    value={selectedDepartment}
                                                    onChange={handleDepartmentChange}
                                                    options={departments}
                                                    placeholder="Select department"
                                                    className={`react-select-container ${errors.department ? 'error' : ''}`}
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.department && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.department}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="phone_number">
                                                    <FaPhone className="field-icon" />
                                                    Phone Number <span className="required">*</span>
                                                </label>
                                                <input
                                                    type="tel"
                                                    id="phone_number"
                                                    name="phone_number"
                                                    value={formData.phone_number}
                                                    onChange={handleChange}
                                                    placeholder="Enter phone number"
                                                    className={errors.phone_number ? 'error' : ''}
                                                />
                                                {errors.phone_number && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.phone_number}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="position">
                                                    <FaBriefcase className="field-icon" />
                                                    Position
                                                </label>
                                                <input
                                                    type="text"
                                                    id="position"
                                                    onChange={handleChange}
                                                    name="position"
                                                    value={formData.position}
                                                    className={errors.position ? 'error' : ''}
                                                />
                                                {errors.position && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.position}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="working_days_per_month">Working Days per Month
                                                </label>
                                                <select
                                                    id="working_days_per_month"
                                                    name="working_days_per_month"
                                                    value={formData.working_days_per_month}
                                                    onChange={handleChange}
                                                    className={errors.working_days_per_month ? 'error' : ''}
                                                >
                                                    <option value="">Select working days</option>
                                                    <option value="22">22</option>
                                                    <option value="26">26</option>
                                                </select>
                                                {errors.working_days_per_month && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.working_days_per_month}
                                                    </div>
                                                )}
                                            </div>

                                        </motion.div>
                                    )}

                                    {currentSlide === 1 && (
                                        <motion.div
                                            key="slide-2"
                                            className="form-slide"
                                            initial={{ opacity: 0, x: '100%' }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: '-100%' }}
                                            transition={{ type: 'tween', duration: 0.3 }}
                                        >
                                            <div className="form-group">
                                                <label htmlFor="contract_type">
                                                    <FaBriefcase className="field-icon" />
                                                    Contract Type <span className="required">*</span>
                                                </label>
                                                <select
                                                    id="contract_type"
                                                    name="contract_type"
                                                    value={formData.contract_type}
                                                    onChange={handleChange}
                                                    className={errors.contract_type ? 'error' : ''}
                                                >
                                                    <option value="open">Open</option>
                                                    <option value="fixed_term">Fixed Term</option>
                                                    <option value="student">Student</option>
                                                </select>
                                                {errors.contract_type && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.contract_type}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="start_date">
                                                    <FaCalendar className="field-icon" />
                                                    Start Date <span className="required">*</span>
                                                </label>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        value={formatDateForInput(formData.contract_start_date)}
                                                        onChange={(date) => handleDateChange(date, 'contract_start_date')}
                                                        format="DD/MM/YYYY"
                                                        slotProps={{
                                                            textField: {
                                                                error: !!errors.contract_start_date,
                                                                helperText: errors.contract_start_date,
                                                                placeholder: "dd/mm/yyyy",
                                                                inputProps: {
                                                                    readOnly: true
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </div>

                                            {(formData.contract_type === 'student' || formData.contract_type === 'fixed_term') && (
                                                <>
                                                <div className="form-group">
                                                    <label htmlFor="end_date">
                                                        <FaCalendar className="field-icon" />
                                                        End Date <span className="required">*</span>
                                                    </label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker
                                                            value={formatDateForInput(formData.contract_end_date)}
                                                            onChange={(date) => handleDateChange(date, 'contract_end_date')}
                                                            format="DD/MM/YYYY"
                                                            minDate={formatDateForInput(formData.contract_start_date)}
                                                            slotProps={{
                                                                textField: {
                                                                    error: !!errors.contract_end_date,
                                                                    helperText: errors.contract_end_date,
                                                                    placeholder: "dd/mm/yyyy",
                                                                    inputProps: {
                                                                        readOnly: true
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>

                                                

                                                </>
                                            )}

                                            {(formData.contract_type === 'fixed_term' || formData.contract_type === 'open') && (
                                                <>
                                                <div className="form-group">
                                                    <label htmlFor="probation_period">
                                                        <FaCalendar className="field-icon" />
                                                        Probation Period (optional)
                                                    </label>
                                                    <select
                                                        name="probation_period"
                                                        id="probation_period"
                                                        value={formData.probation_period}
                                                        onChange={handleChange}
                                                        className={errors.probation_period ? 'error' : ''}
                                                    >
                                                        <option value="">Select probation period</option>
                                                        <option value="1">1 month</option>
                                                        <option value="2">2 months</option>
                                                        <option value="3">3 months</option>
                                                    </select>
                                                    {errors.probation_period && (
                                                        <div className="error-message">
                                                            <FaInfoCircle /> {errors.probation_period}
                                                        </div>
                                                    )}
                                                </div>

                                                {formData.probation_period && formData.contract_start_date && (
                                                    <div className="form-group">
                                                        <label htmlFor="probation_end_date">
                                                            <FaCalendar className="field-icon" />
                                                            Probation End Date
                                                        </label>
                                                        <div style={{
                                                            padding: '0.75rem',
                                                            backgroundColor: '#f3f4f6',
                                                            borderRadius: '0.5rem',
                                                            border: '1px solid #e5e7eb',
                                                            color: '#4b5563',
                                                            fontSize: '0.875rem'
                                                        }}>
                                                            {formatDateForDisplay(formData.probation_end_date)}
                                                        </div>
                                                        <div style={{
                                                            marginTop: '0.5rem',
                                                            fontSize: '0.75rem',
                                                            color: '#6b7280'
                                                        }}>
                                                            {formData.contract_type === 'open' 
                                                                ? 'Automatically calculated: 3 months from contract start date'
                                                                : 'Automatically calculated based on contract start date and probation period'}
                                                        </div>
                                                    </div>
                                                )}
                                                </>)
                                            }

                                            <div className="form-group">
                                                <label htmlFor="salary">
                                                    <FaMoneyBillWave className="field-icon" />
                                                    Salary
                                                </label>
                                                <input
                                                    type="number"
                                                    id="salary"
                                                    name="salary"
                                                    value={formData.salary}
                                                    onChange={handleChange}
                                                    placeholder="Enter salary"
                                                />
                                            </div>

                                            <div className="form-section">
                                                <div className="section-header">
                                                    <FaMoneyBillWave className="section-icon" />
                                                    <h3>Allowances</h3>
                                                </div>
                                                <div className="items-grid">
                                                    {formData.allowances.map((allowance, index) => (
                                                        <div key={index} className="item-card">
                                                            <div className="item-header">
                                                                <h4>Allowance Set {index + 1}</h4>
                                                                {index > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        className="remove-button"
                                                                        onClick={() => handleRemoveAllowance(index)}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="item-fields">
                                                                <div className="form-group">
                                                                    <label>
                                                                        <FaMoneyBillWave className="field-icon" />
                                                                        Type
                                                                    </label>
                                                                    <select
                                                                        value={allowance.allowance_type}
                                                                        onChange={(e) => handleAllowanceChange(index, 'allowance_type', e.target.value)}
                                                                        className={errors[`allowance_${index}_allowance_type`] ? 'error' : ''}
                                                                    >
                                                                        <option value="">Select Allowance Type</option>
                                                                        <option value="housing">Housing</option>
                                                                        <option value="transport">Transport</option>
                                                                        <option value="medical">Medical</option>
                                                                        <option value="phone">Phone</option>
                                                                        <option value="other">Other</option>
                                                                    </select>
                                                                </div>

                                                                {allowance.allowance_type === 'other' && (
                                                                    <div className="form-group">
                                                                        <label>
                                                                            <FaMoneyBillWave className="field-icon" />
                                                                            Allowance Title
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            value={allowance.title || ''}
                                                                            onChange={(e) => handleAllowanceChange(index, 'title', e.target.value)}
                                                                            placeholder="Enter allowance title"
                                                                            className={errors[`allowance_${index}_title`] ? 'error' : ''}
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div className="form-group">
                                                                    <label>
                                                                        <FaMoneyBillWave className="field-icon" />
                                                                        Amount
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        value={allowance.amount}
                                                                        onChange={(e) => handleAllowanceChange(index, 'amount', e.target.value)}
                                                                        placeholder="Enter allowance amount"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="add-button"
                                                        onClick={handleAddAllowance}
                                                    >
                                                        <FaPlus /> Add Allowance Set
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {currentSlide === 2 && (
                                        <motion.div
                                            key="slide-3"
                                            className="form-slide"
                                            initial={{ opacity: 0, x: '100%' }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: '-100%' }}
                                            transition={{ type: 'tween', duration: 0.3 }}
                                        >
                                            <div className="photo-upload-section">
                                                <label>
                                                    <FaUser className="field-icon" />
                                                    Profile Photo
                                                </label>
                                                {formData.photo_preview ? (
                                                    <div className="photo-preview">
                                                        <img src={formData.photo_preview} alt="Preview" />
                                                        <button
                                                            type="button"
                                                            onClick={() => setFormData(prev => ({
                                                                ...prev,
                                                                photo: null,
                                                                photo_preview: null
                                                            }))}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="dropzone"
                                                        onClick={() => document.getElementById('photo-input').click()}
                                                    >
                                                        <div className="upload-placeholder">
                                                            <FaUser />
                                                            <p>Click to upload photo</p>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            id="photo-input"
                                                            accept="image/*"
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                if (e.target.files?.[0]) {
                                                                    const file = e.target.files[0];
                                                                    console.log('File selected:', file);
                                                                    
                                                                    // Store the original file temporarily
                                                                    const reader = new FileReader();
                                                                    reader.onload = () => {
                                                                        // Set the preview and open the dialog
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            photo_preview: reader.result,
                                                                            // Store the original file in case cropping fails
                                                                            photo: file
                                                                        }));
                                                                        
                                                                        // Reset crop and image ref before opening dialog
                                                                        setCrop({ unit: '%', width: 100, aspect: 1 });
                                                                        setImageRef(null);
                                                                        
                                                                        // Open the dialog for cropping
                                                                        setImageDialogOpen(true);
                                                                    };
                                                                    reader.readAsDataURL(file);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="date_of_birth">
                                                    <FaCalendar className="field-icon" />
                                                    Date of Birth <span className="required">*</span>
                                                </label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                value={formatDateForInput(formData.date_of_birth)}
                                                                onChange={(date) => handleDateChange(date, 'date_of_birth')}
                                                                format="DD/MM/YYYY"
                                                                maxDate={dayjs()}
                                                                slotProps={{
                                                                    textField: {
                                                                        error: !!errors.date_of_birth,
                                                                        helperText: errors.date_of_birth || (calculatedAge === null && formData.date_of_birth ? 
                                                                            `Age must be at least ${formData.contract_type === 'student' ? '16' : '18'} years for ${formData.contract_type} contract type` : ''),
                                                                        placeholder: "dd/mm/yyyy",
                                                                        inputProps: {
                                                                            readOnly: true
                                                                        },
                                                                        fullWidth: true
                                                                    }
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </div>
                                                    {calculatedAge !== null && (
                                                        <div style={{
                                                            backgroundColor: '#f3f4f6',
                                                            padding: '0.5rem 1rem',
                                                            borderRadius: '0.5rem',
                                                            fontSize: '0.875rem',
                                                            color: '#4b5563',
                                                            minWidth: '80px',
                                                            textAlign: 'center',
                                                            border: '1px solid #e5e7eb'
                                                        }}>
                                                            Age: {calculatedAge} years
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="gender">
                                                    <FaUser className="field-icon" />
                                                    Gender <span className="required">*</span>
                                                </label>
                                                <select
                                                    id="gender"
                                                    name="gender"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                    className={errors.gender ? 'error' : ''}
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                </select>
                                                {errors.gender && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.gender}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="marital_status">
                                                    <FaUser className="field-icon" />
                                                    Marital Status <span className="required">*</span>
                                                </label>
                                                <select
                                                    id="marital_status"
                                                    name="marital_status"
                                                    value={formData.marital_status}
                                                    onChange={handleChange}
                                                    className={errors.marital_status ? 'error' : ''}
                                                >
                                                    <option value="">Select Marital Status</option>
                                                    <option value="single">Single</option>
                                                    <option value="married">Married</option>
                                                </select>
                                                {errors.marital_status && (
                                                    <div className="error-message">
                                                        <FaInfoCircle /> {errors.marital_status}
                                                    </div>
                                                )}
                                            </div>



                                            <div className="form-group">
                                                <label htmlFor="spouse_contact">
                                                    <FaHome className="field-icon" />
                                                    Home Address
                                                </label>
                                                <input
                                                    type="text"
                                                    id="home_address"
                                                    name="home_address"
                                                    value={formData.home_address}
                                                    onChange={handleChange}
                                                    placeholder="Enter home physical address"
                                                />
                                            </div>

                                             <div className="form-section">
                                                <div className="section-header">
                                                    <FaGraduationCap className="section-icon" />
                                                    <h3>Qualifications</h3>
                                                </div>
                                                <div className="items-grid">
                                                    {formData.qualifications?.map((qualification, index) => (
                                                        <div key={index} className="item-card">
                                                            <div className="item-header">
                                                                <h4>Qualification {index + 1}</h4>
                                                                {index > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        className="remove-button"
                                                                        onClick={() => handleRemoveQualification(index)}
                                                                    >
                                                                        <FaTrash />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="item-fields">
                                                                <div className="form-group">
                                                                    <label>
                                                                        <FaGraduationCap className="field-icon" />
                                                                        Title
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        value={qualification.title}
                                                                        onChange={(e) => handleQualificationChange(index, 'title', e.target.value)}
                                                                        placeholder="Enter qualification title"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="add-button"
                                                        onClick={handleAddQualification}
                                                    >
                                                        <FaPlus /> Add Qualification
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    
{currentSlide === 3 && (
                                        <motion.div
                                            key="slide-4"
                                            className="form-slide"
                                            initial={{ opacity: 0, x: '100%' }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: '-100%' }}
                                            transition={{ type: 'tween', duration: 0.3 }}
                                            style={{ padding: '20px', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                        >
                                            {/* Family Information Section */}
                                            <div className="section-header" style={{ 
                                                marginBottom: '2rem',
                                                borderBottom: '2px solid #e5e7eb',
                                                paddingBottom: '1rem'
                                            }}>
                                                <h3 style={{ 
                                                    fontSize: '1.5rem',
                                                    color: '#1e3a8a',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <FaUser className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                    Next of Kin Details
                                                </h3>
                                            </div>

                                            {/* Next of Kin Information Card */}
                                            <div style={{
                                                backgroundColor: '#f8fafc',
                                                borderRadius: '12px',
                                                padding: '1.5rem',
                                                marginBottom: '2rem',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                            }}>
                                                <h4 style={{
                                                    fontSize: '1.2rem',
                                                    color: '#1e3a8a',
                                                    marginBottom: '1.5rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <FaUser className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                    Next of Kin Details
                                                </h4>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label htmlFor="next_of_kin_name" style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            color: '#4b5563',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            <FaUser className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                            Full Name 
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="next_of_kin_name"
                                                            name="next_of_kin_name"
                                                            value={formData.next_of_kin_name}
                                                            onChange={handleChange}
                                                            placeholder="Enter next of kin name"
                                                            className={errors.next_of_kin_name ? 'error' : ''}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: errors.next_of_kin_name ? '1px solid #e74c3c' : '1px solid #e5e7eb',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                        {errors.next_of_kin_name && (
                                                            <div className="error-message" style={{ color: '#e74c3c', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                                                <FaInfoCircle style={{ marginRight: '0.5rem' }} /> {errors.next_of_kin_name}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label htmlFor="next_of_kin_contact" style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            color: '#4b5563',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            <FaPhone className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                            Contact Number
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            id="next_of_kin_contact"
                                                            name="next_of_kin_contact"
                                                            value={formData.next_of_kin_contact}
                                                            onChange={handleChange}
                                                            placeholder="Enter next of kin contact number"
                                                            className={errors.next_of_kin_contact ? 'error' : ''}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: errors.next_of_kin_contact ? '1px solid #e74c3c' : '1px solid #e5e7eb',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                        {errors.next_of_kin_contact && (
                                                            <div className="error-message" style={{ color: '#e74c3c', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                                                <FaInfoCircle style={{ marginRight: '0.5rem' }} /> {errors.next_of_kin_contact}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label htmlFor="next_of_kin_contact" style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            color: '#4b5563',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            <FaHome className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                            Address
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="next_of_kin_address"
                                                            name="next_of_kin_address"
                                                            value={formData.next_of_kin_address}
                                                            onChange={handleChange}
                                                            placeholder="Enter next of kin address"
                                                            className={errors.next_of_kin_address ? 'error' : ''}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: errors.next_of_kin_contact ? '1px solid #e74c3c' : '1px solid #e5e7eb',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                        {errors.next_of_kin_address && (
                                                            <div className="error-message" style={{ color: '#e74c3c', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                                                <FaInfoCircle style={{ marginRight: '0.5rem' }} /> {errors.next_of_kin_address}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-group" style={{ marginBottom: '0' }}>
                                                        <label htmlFor="next_of_kin_contact" style={{ 
                                                            display: 'block', 
                                                            marginBottom: '0.5rem', 
                                                            color: '#4b5563',
                                                            fontSize: '0.95rem'
                                                        }}>
                                                            <FaPhone className="field-icon" style={{ marginRight: '0.5rem' }} />
                                                                Relationship
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="next_of_kin_relationship"
                                                            name="next_of_kin_relationship"
                                                            value={formData.next_of_kin_relationship}
                                                            onChange={handleChange}
                                                            placeholder="Enter next of kin relationship"
                                                            className={errors.next_of_kin_relationship ? 'error' : ''}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                borderRadius: '0.5rem',
                                                                border: errors.next_of_kin_contact ? '1px solid #e74c3c' : '1px solid #e5e7eb',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                        {errors.next_of_kin_relationship && (
                                                            <div className="error-message" style={{ color: '#e74c3c', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                                                <FaInfoCircle style={{ marginRight: '0.5rem' }} /> {errors.next_of_kin_relationship}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>


                                        </motion.div>
                                    )}

                                    {currentSlide === 4 && (
                                        <motion.div
                                            key="slide-5"
                                            className="form-slide"
                                            initial={{ opacity: 0, x: '100%' }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: '-100%' }}
                                            transition={{ type: 'tween', duration: 0.3 }}
                                        >
                                            <div className="section-header">
                                                <h3 className="section-title">Documents Information</h3>
                                                <button
                                                    type="button"
                                                    className="add-button"
                                                    onClick={handleAddDocument}
                                                    style={{ marginRight: '100px' }}
                                                >
                                                    <FaPlus /> Add Document
                                                </button>
                                            </div>

                                            <div className="items-grid">
                                                {formData.documents.map((doc, index) => (
                                                    <div key={index} className="item-card">
                                                        <div className="item-header">
                                                            <h4>Document {index + 1}</h4>
                                                            {index > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className="remove-button"
                                                                    onClick={() => handleRemoveDocument(index)}
                                                                >
                                                                    <FaTrash />
                                                                </button>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="item-fields">
                                                            <div className="form-group">
                                                                <label htmlFor={`document_${index}_type`}>
                                                                    <FaUser className="field-icon" />
                                                                    Document Type <span className="required">*</span>
                                                                </label>
                                                                <select
                                                                    id={`document_${index}_type`}
                                                                    value={doc.document_type}
                                                                    onChange={(e) => handleDocumentChange(index, 'document_type', e.target.value)}
                                                                    className={errors[`document_${index}_type`] ? 'error' : ''}
                                                                >
                                                                    <option value="">Select Document Type</option>
                                                                    <option value="national_id">National ID</option>
                                                                    <option value="passport">Passport</option>
                                                                    <option value="drivers_license">Driver's License</option>
                                                                    <option value="birth_certificate">Birth Certificate</option>
                                                                    <option value="academic_certificate">Academic Certificate</option>
                                                                    <option value="professional_certificate">Professional Certificate</option>
                                                                </select>
                                                                {errors[`document_${index}_type`] && (
                                                                    <div className="error-message">
                                                                        <FaInfoCircle /> {errors[`document_${index}_type`]}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="form-group">
                                                                <label htmlFor={`document_${index}_file`}>
                                                                    <FaUser className="field-icon" />
                                                                    Document File <span className="required">*</span>
                                                                </label>
                                                                <div className="file-input-container">
                                                                    <input
                                                                        type="file"
                                                                        id={`document_${index}_file`}
                                                                        name={`document_${index}_file`}
                                                                        onChange={(e) => handleFileChange(index, e.target.files[0])}
                                                                        className={errors[`document_${index}_file`] ? 'error' : ''}
                                                                        style={{ display: 'none' }}
                                                                        ref={input => doc.fileInput = input}
                                                                    />
                                                                    <button
                                                                        style={{
                                                                            marginTop: '10px',
                                                                            borderRadius: '7px',
                                                                            padding: '8px',
                                                                            '@media (max-width: 768px)': {
                                                                                marginTop: '5px'
                                                                            }
                                                                        }}
                                                                        type="button"
                                                                        className="file-upload-button"
                                                                        onClick={() => doc.fileInput.click()}
                                                                    >
                                                                        {doc.document_file ? doc.document_file.name : 'Upload Document'}
                                                                    </button>
                                                                    {errors[`document_${index}_file`] && (
                                                                        <div className="error-message">
                                                                            <FaInfoCircle /> {errors[`document_${index}_file`]}
                                                                        </div>
                                                                    )}
                                                                    
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="form-actions">
                                {currentSlide > 0 && (
                                    <button
                                        type="button"
                                        className="prev-button"
                                        onClick={() => handleSlideChange('prev')}
                                    >
                                        <FaArrowLeft /> Previous
                                    </button>
                                )}
                                {currentSlide < 4 && (
                                    <button
                                        type="button"
                                        className="next-button"
                                        onClick={() => handleSlideChange('next')}
                                    >
                                        Next <FaArrowRight />
                                    </button>
                                )}
                                {currentSlide === 4 && (
                                    <button
                                        type="submit"
                                        className="submit-button"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <FaSpinner className="spinner" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave />
                                                Create Employee
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Image Cropping Dialog */}
            <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Crop Image</DialogTitle>
                <DialogContent>
                    {formData.photo_preview && (
                        <>
                            <div className="crop-container">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(newCrop) => setCrop(newCrop)}
                                    onComplete={handleCropComplete}
                                >
                                    <img 
                                        src={formData.photo_preview} 
                                        onLoad={onImageLoad} 
                                        alt="Crop preview" 
                                        style={{ maxHeight: '400px' }}
                                    />
                                </ReactCrop>
                            </div>
                            {croppedImageUrl && (
                                <div className="preview-container" style={{ marginTop: '20px' }}>
                                    <h4>Preview</h4>
                                    <img 
                                        src={croppedImageUrl} 
                                        alt="Cropped preview" 
                                        style={{ height: '100px', width: '100px', border: '1px solid #ddd', borderRadius: '4px' }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImageDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCropSave} 
                        color="primary"
                        disabled={!imageRef || !crop.width || !crop.height}
                    >
                        Save Crop
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CreateEmployee;